import { Room } from '../types';
import { buildOfflineRoom } from './reviewOfflineEntityFactories';

export type ReviewRoomServiceDependencies = {
  cacheService: any;
  database: any;
  offlineReviewService: any;
  onlineApi: {
    getRooms(propertyId?: number): Promise<Room[]>;
    createRoom(propertyId: number, name: string): Promise<Room>;
    updateRoom(roomId: number, data: { name?: string; displayOrder?: number }): Promise<Room>;
    deleteRoom(roomId: number): Promise<void>;
  };
  refreshOnlineState: () => Promise<boolean>;
  getIsOnline: () => boolean;
  useOfflineWorkflow?: () => boolean;
  isWeb: () => boolean;
};

export class ReviewRoomService {
  constructor(private readonly deps: ReviewRoomServiceDependencies) {}

  async getRooms(propertyId?: number): Promise<Room[]> {
    try {
      if (this.deps.cacheService?.getRooms) {
        return await this.deps.cacheService.getRooms(propertyId);
      }

      if (this.deps.getIsOnline()) {
        return await this.deps.onlineApi.getRooms(propertyId);
      }

      return [];
    } catch (error) {
      return [];
    }
  }

  async createRoom(
    propertyId: number,
    name: string,
    options?: { forceOnline?: boolean }
  ): Promise<Room> {
    if (!options?.forceOnline && this.deps.useOfflineWorkflow?.()) {
      return this.createOfflineRoom(propertyId, name);
    }

    const isOnline = await this.deps.refreshOnlineState();

    if (!options?.forceOnline && !isOnline) {
      return this.createOfflineRoom(propertyId, name);
    }

    try {
      const createdRoom = await this.deps.onlineApi.createRoom(propertyId, name);
      await this.cacheCreatedRoom(propertyId, createdRoom);
      return createdRoom;
    } catch (error) {
      if (options?.forceOnline) {
        const existingRoom = await this.findExistingRoomAfterConflict(error, propertyId, name);
        if (existingRoom) {
          return existingRoom;
        }

        throw error;
      }

      if (this.deps.isWeb()) {
        throw error;
      }

      return this.createOfflineRoom(propertyId, name);
    }
  }

  async updateRoom(roomId: number, data: { name?: string; displayOrder?: number }): Promise<Room> {
    return this.deps.onlineApi.updateRoom(roomId, data);
  }

  async deleteRoom(roomId: number): Promise<void> {
    return this.deps.onlineApi.deleteRoom(roomId);
  }

  private async createOfflineRoom(propertyId: number, name: string): Promise<Room> {
    if (this.deps.offlineReviewService?.createOfflineRoom) {
      const tempId = await this.deps.offlineReviewService.createOfflineRoom(propertyId, name);
      return buildOfflineRoom(propertyId, name, tempId);
    }

    throw new Error('Cannot create room offline - service not available');
  }

  private async cacheCreatedRoom(propertyId: number, createdRoom: Room): Promise<void> {
    if (!this.deps.database?.cacheRooms) {
      return;
    }

    const currentRooms = await this.deps.database.getCachedRooms(propertyId);
    const filteredRooms = currentRooms.filter((room: Room) => room.id > 0);
    await this.deps.database.cacheRooms([...filteredRooms, createdRoom], propertyId, 24);
  }

  private async findExistingRoomAfterConflict(
    error: any,
    propertyId: number,
    name: string
  ): Promise<Room | null> {
    if (error?.response?.status !== 409) {
      return null;
    }

    try {
      const existingRooms = await this.deps.onlineApi.getRooms(propertyId);
      return (
        existingRooms.find(room =>
          room.name.trim().toLowerCase() === name.trim().toLowerCase()
        ) || null
      );
    } catch (fetchErr) {
      console.error('[ReviewRoomService] Failed to fetch existing room after 409:', fetchErr);
      return null;
    }
  }
}
